import {MatDialog} from '@angular/material';
import {Component, OnInit} from '@angular/core';
import {BankAccount, BankAccountType} from '../../model/bank.model';
import {Document} from '../../model/document.model';
import {ActivatedRoute, Router} from '@angular/router';
import {UserService} from '../../service/user.service';
import {LoginHelper} from '../../helpers/login.helper';
import {BankService} from '../../service/bank.service';
import {FileService} from '../../service/file.service';
import {Invitation} from '../../model/invitation.model';
import {CreateUserResponse} from '../../model/user.model';
import {UserAddress} from '../../model/user-address.model';
import {PartnerService} from '../../service/partner.service';
import {DocumentService} from '../../service/document.service';
import {InvitationService} from '../../service/invitation.service';
import {LoginInteractor} from '../../interactors/login.interactor';
import {Keys, StorageService} from '../../service/storage.service';
import {InvitationDetails} from '../../model/invitation-details.model';
import {LenderEventsService} from '../../service/lender-events.service';
import {PanVerificationResult} from './pan-verification-status.resolver';
import {LenderDetailsService} from '../../service/lender-details.service';
import {UnprocessableEntityError} from '../../errors/UnprocessableEntityError';
import {AddressProofMapper, DocumentTypeMapper, GenderMapper, Invitee} from '../../model/invitee.model';
import {
    DialogData,
    PanAlreadyPresentPromptComponent
} from '../../common/pan-validator/pan-already-present-prompt/pan-already-present-prompt.component';
import {PreferencesService} from '../../service/preferences.service';
import {Preferences} from '../../model/preferences.model';

@Component({
    selector: 'app-consent',
    templateUrl: './accept-invitation.component.html',
    styleUrls: ['./accept-invitation.component.css', '../../../assets/styles/form-styles.css']
})
export class AcceptInvitationComponent implements OnInit {
    invitationDetails: InvitationDetails;
    phoneVerificationToken: string;
    otpVerificationToken: string;
    otp: string;
    inviteId: string;
    errorMessage: string;
    termsAccepted = false;
    isPhoneVerified: boolean;
    isPanVerified = true;
    isLoadingInProgress: boolean;
    readOnlyTermsAccepted = false;
    panVerificationResult: PanVerificationResult;
    isUserExists = false;
    showConsent = true;
    showMobileOTP = true;
    private dialog: MatDialog;
    private token: string;
    private registeredUser;
    private readonly loginInteractor: LoginInteractor;
    private readonly preferencesService: PreferencesService;

    constructor(private router: Router, private partnerService: PartnerService, protected activatedRoute: ActivatedRoute,
                private invitationService: InvitationService, private storageService: StorageService,
                private userService: UserService, private lenderService: LenderDetailsService, dialog: MatDialog,
                private bankService: BankService, private loginHelper: LoginHelper, private fileService: FileService,
                private documentService: DocumentService, private lenderEventService: LenderEventsService,
                loginInteractor: LoginInteractor, preferencesService: PreferencesService) {
        this.dialog = dialog;
        this.loginInteractor = loginInteractor;
        this.preferencesService = preferencesService;
    }

    async ngOnInit(): Promise<void> {
        this.isLoadingInProgress = true;
        this.isPhoneVerified = false;

        this.activatedRoute.data.subscribe(async data => {
            this.panVerificationResult = data.panVerificationResult;
            this.checkPanVerificationStatus(data.panVerificationResult);

            if (data.invitationDetails) {
                this.invitationDetails = data.invitationDetails.invitationDetails;
                this.inviteId = data.invitationDetails.inviteId;
            }
            this.registeredUser = await this.invitationService.getUserByInviteId(this.inviteId);
            if (!this.registeredUser) {
                this.isUserExists = false;
                this.isLoadingInProgress = false;
            } else {
                this.isLoadingInProgress = true;
                const userId = this.registeredUser._id;
                const {id: loginRequestId, maskedPhoneNumber} = await this.loginInteractor.requestLogIn(userId);
                this.storageService.saveItem(Keys.LOGIN_TOKEN, loginRequestId);
                const dialogRef = this.dialog.open(PanAlreadyPresentPromptComponent, {
                    data: <DialogData>{
                        otp: undefined,
                        phoneNumber: maskedPhoneNumber,
                        loginId: userId
                    }
                });
                const user = await dialogRef.afterClosed().toPromise();
                if (user) {
                    this.token = this.storageService.getItem(Keys.TOKEN);
                }
                this.isLoadingInProgress = true;

                const lender = await this.lenderService.getLenderDetailsByProfileId(userId);
                if (!lender) {
                    this.isPanVerified = true;
                    this.isPhoneVerified = false;
                    this.showMobileOTP = false;
                    this.showConsent = true;
                    this.isLoadingInProgress = false;
                } else {
                    await this.loginHelper.saveLenderDetails(lender);
                    await this.loginHelper.redirectLender(user, lender);
                }
                this.isLoadingInProgress = false;
            }
        });
    }

    async requestPhoneOtp() {
        this.otpVerificationToken = await this.invitationService.requestForPhoneVerification(encodeURIComponent(this.phoneVerificationToken),
            this.inviteId);
        if (!this.otpVerificationToken) {
            this.errorMessage = 'Please generate OTP and verify your mobile number';
        } else {
            this.errorMessage = undefined;
        }
    }

    otpEntered(otp: string) {
        this.otp = otp;
        this.enableSubmitButton();
    }

    enableSubmitButton() {
        this.isPhoneVerified = this.termsAccepted && this.readOnlyTermsAccepted && (!this.showMobileOTP || (this.otp && this.otp.length > 5));
        this.errorMessage = (this.otp && this.otp.length > 5) ? '' : 'Please generate OTP and verify your mobile number';
    }

    onPanVerified(token: string): void {
        this.panVerificationResult = {token, error: null};
        this.checkPanVerificationStatus(this.panVerificationResult);
    }

    async onSubmit() {
        this.isLoadingInProgress = true;

        try {
            let registrationToken = this.token;
            let registeredUser = this.registeredUser;
            if (this.showMobileOTP) {
                registrationToken = await this.userService.verifyPhoneForRegistration(this.otp, this.otpVerificationToken);
                registeredUser = await this.createUserAndLogin(registrationToken, this.inviteId);
            } else {
                registeredUser.userId = this.registeredUser._id;
            }
            // TODO Check if this needs to be pushed to a nested try-catch
            const invitation = await this.updateAndGetInvitation({
                userId: registeredUser.userId,
                authToken: registrationToken
            });

            const partnerReferrer = await this.partnerService.getPartnerByUserId(invitation.invitedBy);
            await this.userService.markOnboardedBy(registeredUser.userId, partnerReferrer.partnerId);

            const lender = await this.createLenderDetails(registeredUser);
            await this.lenderEventService.lenderCreationEvent(this.inviteId, registeredUser.userId);
            await this.associatePersonalInformation(invitation, lender, registeredUser);
            await this.associateResidenceProof(invitation, lender);
            await this.associateBankAccount(invitation, lender, registeredUser.userId);
            await this.associateInvestmentPreferences(registeredUser.userId, invitation.invitee);
            await this.associateDocuments(invitation, lender);
            await this.invitationService.updateStatusByInviteId(this.inviteId, 'ACCEPTED');
            await this.loginHelper.redirectLender(registeredUser, lender);
        } catch (error) {
            if (error instanceof UnprocessableEntityError) {
                this.errorMessage = 'Please enter a valid OTP';
            } else {
                this.errorMessage = 'An error has occurred. Please try again or contact our support team.';
            }
        } finally {
            this.isLoadingInProgress = false;
        }
    }

    public async createUserAndLogin(registrationToken: string, invitationId: string): Promise<CreateUserResponse> {
        const registeredUser = await this.userService.createUser(registrationToken, undefined, undefined, invitationId);
        await this.loginHelper.saveUserDetails({
            name: registeredUser.name,
            _id: registeredUser.userId
        }, registeredUser.authToken);

        return registeredUser;
    }

    public async updateAndGetInvitation(registeredUser: { userId: string; authToken: string }): Promise<Invitation> {
        await this.invitationService.updateUserEmail(this.inviteId, registeredUser.userId);
        return await this.invitationService.verifyAndGetInvitation(this.inviteId);
    }

    initializeView(data: any) {
        this.invitationDetails = data.consentDetails.invitationDetails;
        this.phoneVerificationToken = data.consentDetails.token;
        this.inviteId = data.consentDetails.inviteId;
    }

    checkPanVerificationStatus(panVerificationResult): void {
        if (panVerificationResult) {
            if (panVerificationResult.error) {
                this.isPanVerified = false;
            } else {
                this.phoneVerificationToken = panVerificationResult.token;
                this.isPanVerified = true;
            }
        }
    }

    private async createLenderDetails(registeredUser: CreateUserResponse) {
        const lender = await this.lenderService.createLenderDetails(registeredUser.userId);
        await this.loginHelper.saveLenderDetails(lender);
        return lender;
    }

    private async associatePersonalInformation(invitation: Invitation, lender: any, registeredUser: CreateUserResponse) {
        const address = new UserAddress(invitation.invitee.address.state, Number(invitation.invitee.address.pinCode),
            invitation.invitee.address.city, invitation.invitee.address.addressLine);
        await this.lenderService.addPersonalInformation(lender._id, registeredUser.userId,
            GenderMapper.map(invitation.invitee.gender as string), invitation.invitee.extras.dateOfBirth, address);
    }

    // TODO: write a proper Test for non mandatory addressProofType.
    private async associateResidenceProof(invitation: Invitation, lender: any) {
        if (invitation.invitee.address.addressProofType === null || invitation.invitee.address.addressProofType === undefined) {
            return;
        }
        const proofType = AddressProofMapper.map(DocumentTypeMapper.map(invitation.invitee.address.addressProofType));
        let addressProofNumber;
        if (invitation.invitee.address.addressProofNumber) {
            addressProofNumber = String(invitation.invitee.address.addressProofNumber);
        }

        await this.lenderService.updateResidenceProof(proofType as string, addressProofNumber, lender._id, 33);
    }

    private async associateBankAccount(invitation: Invitation, lender: any, userId: string) {
        const bankInformation = await this.bankService.findByIfsc(invitation.invitee.bankAccount.ifsc);
        const bankAccount = new BankAccount(bankInformation.name, bankInformation.branch, bankInformation.city, bankInformation.ifsc,
            invitation.invitee.bankAccount.accountNumber, invitation.invitee.bankAccount.accountType === BankAccountType.SAVINGS
                ? BankAccountType.Savings : BankAccountType.Current, invitation.invitee.bankAccount.accountHolderName);
        await this.lenderService.addBankAccount(lender._id, bankAccount, userId);
    }

    private async associateDocuments(invitation: Invitation, lender: any) {
        const documentIds: string[] = await Promise.all(invitation.invitee.documents.map(async (document) => {
            const file = await this.fileService.getFileData(document.fileId);
            const newDocument = new Document(DocumentTypeMapper.map(document.documentType), [document.fileId], file.url, file.fileName);
            return await this.documentService.create(newDocument);
        }));

        return await this.lenderService.associateDocuments(lender._id, documentIds);
    }

    private async associateInvestmentPreferences(userId: string, invitee: Invitee) {
        const preferencesForInvitee = invitee.extras && invitee.extras.investmentPreferences ? invitee.extras.investmentPreferences : undefined;
        if (preferencesForInvitee) {
            let preferences: Preferences;
            if(this.isFlexLenderIsHavingFlexPreferences(preferencesForInvitee)) {
                preferences = Preferences.createDefault();
                delete preferences.investmentAttrs;
                preferences.investmentPreferences = preferencesForInvitee;
            } else {
                preferences = <Preferences> preferencesForInvitee;
                const convertedPreferredRatings = Preferences.convertRatingsFromLabels(preferences.preferredRatings);
                preferences.preferredRatings = convertedPreferredRatings;
                const prepayment = <boolean>preferencesForInvitee.reinvest.prepayment;
                preferences.reinvest.prepayment = {
                    reinvestInterest: prepayment,
                    reinvestPrincipal: prepayment
                };
            }
            const savedPreferences = await this.preferencesService.saveUserPreferences(userId, preferences);
            if (savedPreferences) {
                this.storageService.saveItem(Keys.INVESTMENT_PREFERENCES, 'true');
            }
        }
    }

    private isFlexLenderIsHavingFlexPreferences(preferencesForInvitee) {
        if(Array.isArray(preferencesForInvitee) && preferencesForInvitee.length) {
            return true;
        }
        return false;
    }
}
